import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";

export const formatDashboardData = (data: any) => {
  let formattedData: any = {
    stats: [
      {
        id: uuid(),
        value: data?.total_sites,
        widgetKey: "total_sites",
        title: "Sites",
        desc: "Total Sites",
        color: "#4A1D96",
      },
      {
        id: uuid(),
        value: data?.total_participants,
        widgetKey: "total_participants",
        title: "Participants",
        desc: "Total Participants",
        color: "#0694A2",
      },
      {
        id: uuid(),
        value: data?.total_adverse_events,
        widgetKey: "total_adverse_events",
        title: "Adverse Events",
        desc: "Total Adverse Events",
        color: "#0E9F6E",
      },
      {
        id: uuid(),
        value: data?.total_queries,
        widgetKey: "total_queries",
        title: "Queries",
        desc: "Total queries",
        color: "#4A1D96",
      },
      {
        id: uuid(),
        value: data?.total_locked_participants,
        widgetKey: "total_locked_participants",
        title: "Locked Participants",
        desc: "Total Locked Participants",
        color: "#3F83F8",
      },
      {
        id: uuid(),
        value: data?.total_locked_forms,
        widgetKey: "total_locked_forms",
        title: "Locked Forms",
        desc: "Total Locked Forms",
        color: "#9061F9",
      },
      {
        id: uuid(),
        value: data?.total_open_queries,
        widgetKey: "total_open_queries",
        title: "Queries Pending",
        desc: "Queries Pending",
        color: "#0694A2",
      },
      {
        id: uuid(),
        value: data?.average_time_to_close_queries,
        widgetKey: "average_time_to_close_queries",
        title: "Average Time to Close",
        desc: "Average Time to Close",
        color: "#0E9F6E",
      },
      {
        id: uuid(),
        value: data?.average_open_queries_per_site,
        widgetKey: "average_open_queries_per_site",
        title: "Average Pending Per Site",
        desc: "Average Pending Per Site",
        color: "#F98080",
      },
    ].filter((item) => item.value !== null && item.value !== undefined),
    participantInsights: data?.participant_insights?.map((item: any) => {
      return {
        siteId: item?.siteId ?? "-",
        siteName: item?.siteName ?? "-",
        total: item?.total_participants ?? "-",
        included: item?.total_included_participants ?? "-",
        excluded: item?.total_excluded_participants ?? "-",
        randomized: item?.total_randomized_participants ?? "-",
      };
    }),
    participantCreationOverTime: data?.participant_creation_over_time
      ? formatParticipantGraph(data?.participant_creation_over_time)
      : null,
    participantsBySite: data?.participants_by_site?.map((item: any) => {
      return {
        siteId: item?.siteId ?? "-",
        siteName: item?.siteName ?? "-",
        totalParticipants: item?.total_participants ?? "-",
      };
    }),
    queriesBySite: data?.queries_by_site
      ? formatQueriesData(data?.queries_by_site)
      : null,
    randomizedParticipantsBySite: data?.randomized_participants_by_site?.map(
      (item: any) => {
        return {
          siteId: item?.siteId ?? "-",
          siteName: item?.siteName ?? "-",
          totalParticipants: item?.total_randomized_participants ?? "-",
        };
      }
    ),
    participantsList3Months: data?.participant_list_for_3month_view_reminders
      ? formatParticipantList(data?.participant_list_for_3month_view_reminders)
      : null,
    participantsList6Months: data?.participant_list_for_6month_view_reminders
      ? formatParticipantList(data?.participant_list_for_6month_view_reminders)
      : null,
    participantsList12Months: data?.participant_list_for_12month_view_reminders
      ? formatParticipantList(data?.participant_list_for_12month_view_reminders)
      : null,
    surveyPackages: data?.survey_package_completion?.map(
      (surveyPackage: any) => {
        return {
          id: surveyPackage?.package_id ?? "-",
          name: surveyPackage?.package_name ?? "-",
          completed: surveyPackage?.completed ?? "-",
          inProgress: surveyPackage?.in_progress ?? "-",
        };
      }
    ),
    queryOverview: data?.queries_overview
      ? formatQueriesData(data?.queries_overview)
      : null,
    maxQueryY: data?.queries_overview
      ? findQueryYMax(data?.queries_overview)
      : 5,
    isEmpty: data?.queries_overview
      ? findQueryIsEmpty(data?.queries_overview)
      : true,
    mostQueriedForms: data?.form_rank_by_queries?.map((form: any) => ({
      id: uuid(),
      name: form?.name,
      totalQueries: form?.query_count,
    })),
  };
  return formattedData;
};

const formatParticipantList = (data: any) => {
  let formattedData: any[] = [];
  const transformedData =
    data?.map((item: any) => ({
      id: item.id,
      subjectId: item.subject_id,
      textValue: item.text_value,
      diffDays: item.diff_days,
    })) || [];

  for (let i = 0; i < transformedData.length; i += 3) {
    let row = transformedData.slice(i, i + 3);
    while (row.length < 3) {
      row.push({
        id: "",
        subjectId: "",
        textValue: "",
        diffDays: "",
      });
    }

    formattedData.push(row);
  }
  return formattedData;
};

const formatQueriesData = (data: any[]) => {
  let formattedData: any[] = [];
  if (data?.length === 0) return formattedData;
  formattedData = data?.map((item: any) => ({
    siteId: item?.siteId,
    siteName: item?.siteName,
    totalQueries: item?.total_queries,
    Open: item?.total_open_queries,
    Closed: item?.total_closed_queries,
    Resolved: item?.total_resolved_queries,
    averageCloseTime: item?.average_query_close_time,
  }));
  return formattedData;
};

const findQueryYMax = (data: any[]): number => {
  let max = 5;
  const maxOpen = Math.max(...data.map((item) => item.total_open_queries));
  const maxClosed = Math.max(...data.map((item) => item.total_closed_queries));
  const maxResolved = Math.max(
    ...data.map((item) => item.total_resolved_queries)
  );
  max = Math.max(maxOpen, maxClosed, maxResolved);
  return max > 5 ? max + 1 : 5;
};

const findQueryIsEmpty = (data: any[]): boolean => {
  let isEmpty = true;
  if (data?.length === 0) return isEmpty;
  const totalQueryCount = data?.reduce(
    (acc, item) => acc + item.total_queries,
    0
  );
  if (totalQueryCount > 0) {
    isEmpty = false;
  }
  return isEmpty;
};

export const formatParticipantGraph = (data: any[]) => {
  let formattedData: any[] = [
    {
      data: [],
      id: "",
    },
  ];

  if (data?.length === 0) return formattedData;

  let days: { [key: string]: number } = {};

  data?.forEach((item: any) => {
    const date = item?.date_time?.split("T")?.[0];
    if (date) {
      days[date] = item?.count;
    }
  });

  const start = DateTime.fromFormat(
    data?.[0]?.date_time?.split("T")?.[0],
    "yyyy-MM-dd"
  );
  const lastDataDate = DateTime.fromFormat(
    data?.[data.length - 1]?.date_time?.split("T")?.[0],
    "yyyy-MM-dd"
  );
  const end = lastDataDate;

  const daysDiff = Math.ceil(end.diff(start, "days").get("days")) + 1;

  const newData = [];
  for (let i = 0; i < daysDiff; i++) {
    const currentDate = start.plus({ days: i });
    const key = currentDate.toFormat("yyyy-MM-dd");
    const count = days?.[key] ?? 0;

    newData.push({
      x: key,
      y: count,
    });
  }

  formattedData = [
    {
      data: newData,
      id: "Participants",
    },
  ];

  return formattedData;
};

export const DashboardCards = [
  {
    key: "total_participants",
    value: 0,
    title: "Total participants",
    desc: "Total Potential Participants",
    color: "#FFCA54",
  },
  // {
  //   key: "total_consents_signed",
  //   value: 0,
  //   title: "Consent",
  //   desc: "Total Consented",
  //   color: "#9DA0C4",
  // },
  // {
  //   key: "total_enrolled",
  //   value: 0,
  //   title: "Enrolled",
  //   desc: "Total Enrolled",
  //   color: "#84E1BC",
  // },
  // {
  //   key: "total_scheduled_appointments",
  //   value: 0,
  //   title: "Appointments",
  //   desc: "Total Appointments",
  //   color: "#F98080",
  // },
  {
    key: "total_screened_responses",
    value: 0,
    title: "Screened Responses",
    desc: "Total Screened Responses",
    color: "#3F83F8",
  },
];

export const ParticipantDashboardCards = [
  {
    key: "total_participants",
    value: 0,
    title: "Total Participants",
    desc: "Total Participants",
    color: "#FFCA54",
  },
  {
    key: "merged",
    value: 0,
    title: "Merged Records",
    desc: "Merged Records",
    color: "#84E1BC",
  },
  {
    key: "unique",
    value: 0,
    title: "Unique Records",
    desc: "Unique Records",
    color: "#F98080",
  },
  {
    key: "pendingReview",
    value: 0,
    title: "Pending Review",
    desc: "Pending Review",
    color: "#9DA0C4",
  },
];

export const CardTypes = [
  "total_participants",
  // "total_consents_signed",
  // "total_enrolled",
  // "total_scheduled_appointments",
  "total_screened_responses",
];

export const ParticipantCardTypes = [
  "total_participants",
  "high_similarity_pairs",
  "duplicate_pairs",
  "unique_pairs",
];

export const findYMax = (data: any) => {
  const numbers = data?.[0]?.data;
  let max = 10;
  for (let i = 0; i < numbers?.length; i++) {
    if (numbers?.[i]?.y > max) {
      max = numbers?.[i]?.y;
    }
  }
  return max;
};

export const groupPieColors = [
  "#3B82F6",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#EC4899",
  "#6366F1",
  "#14B8A6",
  "#F97316",
];

export const formatGroupPieData = (data: any[]) => {
  const totalCount = data?.reduce(
    (sum: number, group: any) => sum + group.count,
    0
  );
  const formattedPieData = data?.map((item: any, index: number) => {
    return {
      id: (index + 1).toString(),
      label: item?.label,
      name: item?.label,
      value: parseFloat(((item?.count / totalCount) * 100).toFixed(2)) || 0,
      valueLabel: item?.count || 0,
      color: groupPieColors?.[index],
    };
  });
  return {
    data: formattedPieData,
    title: "Total Participant Across Groups",
    totalCount,
  };
};

export const formatGenderData = (data: any) => {
  const totalCount = data?.reduce(
    (sum: number, group: any) => sum + group.count,
    0
  );
  const formattedPieData = data?.map((item: any, index: number) => {
    return {
      id: (index + 1).toString(),
      label: item?.label,
      value: item?.countPercentage || 0,
      valueLabel: item?.count || 0,
      color: groupPieColors?.[index],
    };
  });
  return {
    data: formattedPieData,
    title: "Participants by Gender",
    totalCount,
  };
};

export const formatAgeGroupData = (data: any) => {
  const newData = data?.map((item: any) => {
    return {
      date: item.date.split("T")[0],
      group: item?.group,
      count: item?.count,
    };
  });
  return {
    data: newData,
    title: "Participants by Age Group",
  };
};

export const formatRegistryGenderData = (data: any) => {
  const GENDER_LABELS: { [key: string]: string } = {
    male: "Male",
    female: "Female",
    other: "Other",
    preferNotToSay: "Prefer not to say",
    notSpecified: "Not Specified",
  };
  const pieData = Object.keys(data).map((key) => ({
    label: GENDER_LABELS[key] || key,
    count: data[key] || 0,
  }));
  const totalCount = pieData.reduce(
    (sum: number, group: any) => sum + group.count,
    0
  );
  const formattedPieData = pieData.map((item: any, index: number) => {
    const percentage = totalCount > 0 ? (item.count / totalCount) * 100 : 0;
    return {
      id: (index + 1).toString(),
      label: item?.label,
      value: Number(percentage.toFixed(2)),
      valueLabel: item?.count || 0,
      color: groupPieColors?.[index],
    };
  });
  return {
    data: formattedPieData,
    title: "Participants by Gender",
    totalCount,
  };
};

export const formatRegistryAgeGroupData = (data: any) => {
  const AGE_GROUP_KEYS = [
    "0-25",
    "26-35",
    "36-45",
    "46-55",
    "56-65",
    "66-75",
    "76-85",
    ">85",
    "notSpecified",
  ];
  const AGE_GROUP_LABELS: { [key: string]: string } = {
    notSpecified: "Not Specified",
  };
  const counts = data ? Object.values(data).map((val) => val as number) : [0];
  const maxCount = Math.max(...counts, 0);
  let yMax = Math.ceil(maxCount / 10) * 10;
  const yMin = 0;

  if (yMax === 0) {
    yMax = 50;
  }
  const barData = data
    ? AGE_GROUP_KEYS.map((key) => ({
        group: AGE_GROUP_LABELS[key] || key,
        count: data[key] || 0,
      }))
    : [];
  return {
    data: barData,
    title: "Participants by Age Group",
    yMin,
    yMax,
  };
};

export const formatFunnelPieData = (participantGroups: any[]) => {
  const aggregatedData = participantGroups?.reduce(
    (acc, group) => ({
      total_participants: acc.total_participants + (group.count || 0),
      total_consented: acc.total_consented + (group.consentedParticipants || 0),
      total_scheduled: acc.total_scheduled + (group.scheduledAppointments || 0),
      total_completed: acc.total_completed + (group.completedAppointments || 0),
    }),
    {
      total_participants: 0,
      total_consented: 0,
      total_scheduled: 0,
      total_completed: 0,
    }
  ) || {
    total_participants: 0,
    total_consented: 0,
    total_scheduled: 0,
    total_completed: 0,
  };

  const funnelData = [
    {
      id: "1",
      label: "Participants",
      key: "total_participants",
      color: "#1C64F2",
    },
    {
      id: "2",
      label: "Consented",
      key: "total_consented",
      color: "#0E9F6E",
    },
    {
      id: "3",
      label: "Scheduled",
      key: "total_scheduled",
      color: "#7E3AF2",
    },
    {
      id: "4",
      label: "Completed",
      key: "total_completed",
      color: "#E74694",
    },
  ];

  const totalFunnelCount = funnelData.reduce(
    (sum: number, item: any) => sum + (aggregatedData[item.key] ?? 0),
    0
  );

  const funnelPieData = funnelData.map((item: any) => {
    const count = aggregatedData[item.key] ?? 0;
    const percentage = totalFunnelCount ? (count / totalFunnelCount) * 100 : 0;

    return {
      id: item.id,
      label: item.label,
      value: Number(percentage.toFixed(2)),
      valueLabel: count,
      color: item.color,
    };
  });

  return {
    data: funnelPieData,
    totalParticipants: aggregatedData.total_participants.toLocaleString(),
    totalFunnelCount,
    title: "Participant Funnel",
  };
};

export const CountCards = [
  {
    key: "totalStudies",
    value: 0,
    title: "Total Studies",
  },
  {
    key: "totalRegisteredParticipants",
    value: 0,
    title: "Total Participants in Registry",
  },
  {
    key: "totalActiveStudies",
    value: 0,
    title: "Active Studies",
  },
  {
    key: "totalRegistryTemplates",
    value: 0,
    title: "Total Registry Templates",
  },
  {
    key: "totalConsentTemplates",
    value: 0,
    title: "Consent Templates",
  },
  {
    key: "totalScreenerTemplates",
    value: 0,
    title: "Screener Templates",
  },
  {
    key: "totalDataSheetTemplates",
    value: 0,
    title: "Data sheet Templates",
  },
];

export const formatTableData = (data: any) => {
  const keysToSum = [
    "targetCount",
    "count",
    "consentedParticipants",
    "scheduledAppointments",
    "completedAppointments",
  ] as const;

  const totals = data?.reduce(
    (acc: any, item: any) => {
      keysToSum.forEach((key) => {
        acc[key] = (acc[key] as number) + (item[key] as number);
      });
      return acc;
    },
    {
      label: "Total",
      targetCount: 0,
      count: 0,
      consentedParticipants: 0,
      scheduledAppointments: 0,
      completedAppointments: 0,
    } as any
  );

  const newTableData = data?.length > 0 ? [...data, totals] : [];
  return newTableData;
};
